// Canvas can't inherit CSS custom properties, so every chart resolves its
// colours from the mounted container and re-resolves when the theme flips.
// Extracted from NwLineChart so one bridge serves every chart primitive —
// two copies of this is exactly the drift docs/charts.md exists to prevent.

import { useEffect, useMemo, useRef, useState } from 'react'

/** Read a CSS custom property off `el`, falling back when it is unset. */
export function cssVar(el: Element, name: string, fallback: string): string {
  const v = getComputedStyle(el).getPropertyValue(name).trim()
  return v || fallback
}

/** hex/rgb color → rgba() at the given alpha (area fills, hover rings). */
export function withAlpha(color: string, alpha: number): string {
  const hex = color.match(/^#([0-9a-f]{6})$/i)?.[1]
  if (hex) {
    const n = parseInt(hex, 16)
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
  }
  const rgb = color.match(/^rgba?\(([^)]+)\)/)?.[1]
  if (rgb) {
    const [r, g, b] = rgb.split(',').map((s) => s.trim())
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }
  return color
}

/** Re-render trigger when the theme class flips on the nearest theme root
 *  (`.nw-root` in the widget's shadow DOM; `<html>` on a bare host). */
export function useThemeVersion(containerRef: React.RefObject<HTMLDivElement | null>): number {
  const [version, setVersion] = useState(0)
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const root = el.closest('.nw-root') ?? document.documentElement
    const mo = new MutationObserver(() => setVersion((v) => v + 1))
    mo.observe(root, { attributes: true, attributeFilter: ['class'] })
    return () => mo.disconnect()
  }, [containerRef])
  return version
}

/** Var name + fallback for one colour role. */
export type TokenSpec = readonly [name: string, fallback: string]

export interface ChartTokens {
  ink: string
  faint: string
  grid: string
  threshold: string
  surface: string
  series: string[]
}

export const DEFAULT_TOKENS = {
  ink: ['--ink', '#e7ecf3'],
  faint: ['--faint', '#5e6b7c'],
  grid: ['--line2', 'rgba(255,255,255,0.12)'],
  threshold: ['--red', '#d9605c'],
  surface: ['--card', '#141b25'],
} as const

/** Resolve chart chrome + per-series colours against the mounted container.
 *
 *  First render is pre-mount and uses the fallbacks; the mounted re-render
 *  swaps in the real tokens, and a theme class flip re-resolves everything.
 *  Returns the wrapper ref the caller must attach.
 */
export function useChartTokens(chrome: {
  inkVar: string
  faintVar: string
  gridVar: string
  thresholdVar: string
  surfaceVar: string
  series: readonly TokenSpec[]
}): { wrapRef: React.RefObject<HTMLDivElement | null>; colors: ChartTokens } {
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const themeVersion = useThemeVersion(wrapRef)
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  const el = wrapRef.current
  const { inkVar, faintVar, gridVar, thresholdVar, surfaceVar, series } = chrome
  const seriesKey = series.map(([n, f]) => `${n}|${f}`).join(',')
  const colors = useMemo(() => {
    const resolve = (name: string, fb: string) => (el ? cssVar(el, name, fb) : fb)
    return {
      ink: resolve(inkVar, DEFAULT_TOKENS.ink[1]),
      faint: resolve(faintVar, DEFAULT_TOKENS.faint[1]),
      grid: resolve(gridVar, DEFAULT_TOKENS.grid[1]),
      threshold: resolve(thresholdVar, DEFAULT_TOKENS.threshold[1]),
      surface: resolve(surfaceVar, DEFAULT_TOKENS.surface[1]),
      series: series.map(([name, fb]) => resolve(name, fb)),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- themeVersion/mounted force re-resolution
  }, [el, mounted, themeVersion, inkVar, faintVar, gridVar, thresholdVar, surfaceVar, seriesKey])

  return { wrapRef, colors }
}
